/**
 * Stable per-browser identifier for analytics dedup (ad impressions, clicks).
 *
 * Why this exists: server-side `request.client.host` returns the Kubernetes
 * ingress IP, so all real visitors collapsed into a handful of cluster proxy
 * IPs and "unique impressions" was meaningless. The backend now reads an
 * `X-Viewer-Id` header and dedupes per (ad, viewer, day). This helper
 * generates a UUID once per browser and persists it in localStorage so the
 * same person isn't re-counted on every page refresh.
 *
 * Privacy: the UUID is opaque, contains no PII, and never leaves the user's
 * browser except as an analytics dedup key. It is not used for tracking,
 * targeting, or cross-site identification.
 */
const STORAGE_KEY = 'de_viewer_id';

const generateUuid = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers / non-secure contexts
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

export const getViewerId = () => {
  try {
    let id = localStorage.getItem(STORAGE_KEY);
    if (!id) {
      id = generateUuid();
      localStorage.setItem(STORAGE_KEY, id);
    }
    return id;
  } catch {
    // localStorage unavailable (private mode / disabled) — return a per-tab id
    if (!window.__viewerIdFallback) window.__viewerIdFallback = generateUuid();
    return window.__viewerIdFallback;
  }
};

/** Convenience: axios config object that adds the viewer id header. */
export const viewerHeaders = () => ({ headers: { 'X-Viewer-Id': getViewerId() } });
